import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';
import { useAccessibilityContext, DEFAULT_ZOOM_FACTOR, INCREASED_TEXT_LEVEL } from '@vmos2/providers';
import { scaleCSSUnit } from '@vmos2/utils';

type Props = {
  ariaHidden: boolean,
  /**
   * Content importance. Match with `<h1>`, `<h2>`, `<h3>`. You can define all levels for custom theme mode
   */
  level: 1 | 2 | 3,
  className: ?string,
  children: React.Node,
  id?: string,
  mode?: string,
  tag?: string,
  inValid?: boolean,
  margin: 'none' | 'xsmall' | 'small' | 'normal' | 'large',
  align?: 'center' | 'right' | 'left' | 'spaceBetween',
  /**
   * Color prop will override other colors from component's theme mode or `inValid` prop
   */
  color?: string,
  display: 'block' | 'flex' | 'none',
  noWrap?: boolean,
  initialFont?: boolean,
  dataTest?: string,
  gap?: string,
  alignItems?: string,
  ariaHidden?: Boolean,
  ignoreZoomScaling?: boolean,
  autoFocus?: boolean,
};

/**
 * ### Available modes
 * * `HomeCategory`: Title from the Menu Overview page
 * * `LoyaltyAuthCardLoggedIn`: Title within the LoyaltyAuthCard component on the checkout page when logged in
 * * `LoyaltyAuthCardLoggedOut`: Title within the LoyaltyAuthCard component on the checkout page when logged out
 * * `MenuCategory`: Title from the Menu Category page
 * * `MealCategory`: Title from the Meal Builder page category / upsell category
 * * `MealDealCategory`: Title from the Meal Builder Category page
 * * `QrScanModal`: Title within the Modal when scanning QR codes
 * * `StoreSelection`: Title from the Store Selection page
 * * `StoreSelectionDescription`: Description from the Store Selection page
 * * `PanelHeader`: All panel headers
 * * `EmptyCart`: Title for empty cart on basket page
 * * `OurStores`: Title for 'Our stores' on home page
 * * `BackButtonHeader`: Title inside header on Basket, Account, Order Summary pages
 * * `Introduction`: Title on the Introduction page
 * * `Welcome`: Titles on the Welcome page
 * * `MealDealSummary`: Titles on the Meal Deal Summary page
 * * `BundleCustomization`: Titles on the Bundle Customization Page and Sub Page
 * * `Modal`: Titles on all modals
 * * `LinearHeader`: Titles on the Meal Deal page when linear mode is enabled
 *
 * ### Example
 * ```
  default {
    ...
    Title: {
      HomeCategory: {
        secondaryFontFamily: false,
      },
      MenuCategory: {
        secondaryFontFamily: false,
      }
    }
    ...
  }
 * ```
 * */

const _Title = ({
  ariaHidden = false,
  level,
  className,
  color,
  children,
  id,
  mode,
  tag,
  inValid,
  margin,
  align,
  display,
  noWrap,
  initialFont,
  dataTest,
  gap,
  alignItems,
  ignoreZoomScaling,
  autoFocus = false,
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Title, mode);
  const { zoomFactor } = useAccessibilityContext();

  const isZoomed = !!zoomFactor && zoomFactor !== DEFAULT_ZOOM_FACTOR;
  const Tag = tag || `h${level}`;

  const titleRef = useRef<HTMLHeadingElement>(null);
  const shouldAutoFocusOnMount = autoFocus && !ariaHidden;

  useEffect(() => {
    if (titleRef?.current && shouldAutoFocusOnMount) {
      titleRef.current?.focus();
    }
  }, [shouldAutoFocusOnMount]);

  return (
    <Heading
      {...(titleRef && shouldAutoFocusOnMount && { tabIndex: -1 })}
      ref={titleRef}
      as={Tag}
      className={className}
      id={id}
      level={level}
      componentTheme={componentTheme}
      inValid={inValid}
      marginType={margin}
      align={componentTheme.align || align}
      color={color}
      display={display}
      noWrap={noWrap}
      initialFont={initialFont}
      data-test={dataTest}
      gap={gap}
      alignItems={alignItems}
      ignoreZoomScaling={ignoreZoomScaling}
      isZoomed={isZoomed}
      {...(ariaHidden && { 'aria-hidden': true })}
    >
      {children}
    </Heading>
  );
};

const marginStyles = {
  xsmall: 'margin: 0 0 8px',
  small: 'margin: 0 0 16px',
  normal: 'margin: 0 0 24px',
  large: 'margin: 0 0 32px',
  none: 'margin: 0',
};

const alignStyles = {
  center: 'text-align: center',
  left: 'text-align: left',
  right: 'text-align: right',
  spaceBetween: 'display: flex; justify-content: space-between',
  itemsCenter: `display: flex; align-items: center`,
};

const Heading = styled('h3')`
  display: ${({ display }) => display};
  font-weight: ${({ componentTheme }) => componentTheme.fontWeight};
  color: ${({ componentTheme, theme, inValid, color }) =>
    color || (inValid ? theme.color.danger : componentTheme.color)};
  font-size: ${({ componentTheme, level, ignoreZoomScaling, isZoomed }) =>
    ignoreZoomScaling && isZoomed
      ? scaleCSSUnit(componentTheme.fontSize[`level${level}`], INCREASED_TEXT_LEVEL)
      : componentTheme.fontSize[`level${level}`]};
  font-family: ${({ theme, componentTheme, initialFont }) =>
    componentTheme.secondaryFontFamily && !initialFont ? theme.typography.secondaryFontFamily : 'inherit'};
  ${({ marginType }) => marginStyles[marginType]};
  ${({ align }) => align && alignStyles[align]};
  text-transform: ${({ componentTheme }) => componentTheme.textTransform};
  ${({ noWrap }) => noWrap && 'white-space: nowrap'};
  ${({ gap }) => gap && `gap: ${gap}`};
`;

_Title.defaultProps = {
  className: null,
  margin: 'normal',
  display: 'block',
  initialFont: false,
  level: 3,
};

export const Title = withSpacingStyles(_Title);
